import getAgendaStore from '../store/store';
import { AgendaItem } from 'mini-agenda';
import { AnchorElement, ScrollContainerCoordinateRef } from 'mini-infinite-scroll';

export interface AnchorKeyInfo {
    anchorKey: string; // Anchor element key
    timestamp: number; // Timestamp of the event represented in anchor element
    isOnTop: boolean; // Is anchor element position is relative to the top
    offsetFromTop: number; // Offset relative to top or bottom
}

let anchorKeyInfo: AnchorKeyInfo = {
    anchorKey: null,
    timestamp: null,
    isOnTop: true,
    offsetFromTop: 0,
};

/**
 * Sets anchor element info
 * @param anchorKey Anchor element key
 * @param timestamp Timestamp of the event represented in anchor element
 * @param isOnTop Is anchor element position is relative to the top
 * @param offsetFromTop Offset relative to top or bottom
 */
export function setAnchorKeyInfo(
    anchorKey: string,
    timestamp: number,
    isOnTop?: boolean,
    offsetFromTop?: number
) {
    anchorKeyInfo = {
        anchorKey: anchorKey,
        timestamp: timestamp,
        isOnTop: !!isOnTop,
        offsetFromTop: offsetFromTop || 0,
    };
}

/**
 * Updates anchor element relative position to viewport and it's offset
 * @param isOnTop Whether the anchor element's position should be maintained relative to top or bottom
 * @param offsetFromTop Offset relative to top
 */
export function updateAnchorKeyPosition(isOnTop: boolean, offsetFromTop?: number) {
    if (!anchorKeyInfo.anchorKey) {
        throw Error('Should set valid anchor element before updating the position');
    }
    anchorKeyInfo = {
        ...anchorKeyInfo,
        isOnTop: !!isOnTop,
        offsetFromTop: offsetFromTop || 0,
    };
}

/**
 * Returns timestamp of the anchor element
 */
export function getAnchorKeyInfo(): AnchorKeyInfo {
    return anchorKeyInfo;
}

/**
 * Gets anchor element to be used in ISV
 */
export function getAnchorElement(): AnchorElement {
    const { items: agendaItems } = getAgendaStore();
    const { anchorKey, isOnTop, offsetFromTop } = anchorKeyInfo;
    const anchorElem: AnchorElement = {
        elemSelector: '',
    };
    const isAnchorKeyValid = agendaItems.some(item => item.id === anchorKey);

    if (isAnchorKeyValid) {
        anchorElem.elemSelector = createAnchorKeySelector(anchorKey);
        isOnTop &&
            (anchorElem.observationPoint = {
                reference: isOnTop
                    ? ScrollContainerCoordinateRef.TOP
                    : ScrollContainerCoordinateRef.BOTTOM,
                displacement: offsetFromTop || 0,
            });
    } else {
        const anchorKey = getNextClosestAnchorKey(agendaItems);
        const anchorKeyElemSelector = anchorKey ? createAnchorKeySelector(anchorKey) : null;
        anchorElem.elemSelector = anchorKeyElemSelector;
    }

    return anchorElem;
}

/**
 * Returns the itemId closest to the current anchor element
 */
function getNextClosestAnchorKey(items: AgendaItem[]): string {
    const { timestamp } = anchorKeyInfo;
    let closestAnchorElem: AgendaItem = items.length > 0 ? items[items.length - 1] : null;
    for (let i = 0; i < items.length; i++) {
        if (items[i].timestamp === timestamp || items[i].timestamp > timestamp) {
            closestAnchorElem = items[i];
            break;
        }
    }
    return closestAnchorElem && closestAnchorElem.id;
}

function createAnchorKeySelector(anchorKey: number | string) {
    return anchorKey && `div[data-agendaitem="${anchorKey}"]`;
}
